export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  image: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

// Real stock photographs (Pexels CDN) chosen to match each specific service.
// Client gallery photos are hardscaping/retaining walls only, so they are not
// used for these service cards.
// Self-hosted (optimized WebP) copies of the stock photos — avoids third-party
// image cookies and keeps the assets under our control. See public/assets/images/.
const IMG = (id: number) => `/assets/images/pexels-${id}.webp`;

export const services: ServiceData[] = [
  {
    slug: "lawn-mowing",
    title: "Lawn Mowing",
    subtitle: "Cutting, blowing & weeding",
    price: "Free Estimate",
    image: IMG(4162016),
    description:
      "Reliable, professional lawn mowing across Johnson County, KS. Every visit includes a clean, even cut, edging and blowing of driveways, walkways, and patios, plus weeding so your yard always looks sharp. We keep your grass at the ideal height for a healthy, green lawn all season long.",
    features: [
      "Precision mowing at the right height for your turf",
      "Edging along beds, walks & driveways",
      "Blowing clippings off all hard surfaces",
      "Weeding to keep the yard tidy",
      "Weekly & bi-weekly recurring schedules",
      "Reliable, on-time, fully insured crews",
    ],
    process: [
      { step: "Walkthrough", desc: "We review your property, turf type, and any problem areas." },
      { step: "Custom Plan", desc: "You get a mowing schedule and flat-rate quote that fits your yard." },
      { step: "Mow & Detail", desc: "We cut, edge, weed, and blow every visit — start to finish." },
      { step: "Final Check", desc: "We walk the yard to make sure it's clean and looks its best." },
    ],
  },
  {
    slug: "landscape-maintenance",
    title: "Landscape Maintenance",
    subtitle: "Mulching, trimming & planting",
    price: "Free Estimate",
    image: IMG(11534117),
    description:
      "Keep your landscaping healthy and beautiful year-round. Our maintenance service covers fresh mulching to protect your beds and retain moisture, trimming and pruning of shrubs and hedges, and planting new shrubs to fill in and refresh your yard across Olathe, Overland Park, and the surrounding area.",
    features: [
      "Fresh mulch installation & top-ups",
      "Shrub & hedge trimming and shaping",
      "Pruning for healthy, controlled growth",
      "Planting new shrubs & greenery",
      "Seasonal cleanups & refreshes",
      "Curb-appeal upgrades for homes & businesses",
    ],
    process: [
      { step: "Site Visit", desc: "We assess your beds, shrubs, and goals for the space." },
      { step: "Custom Quote", desc: "A clear, flat-rate quote for mulching, trimming, and planting." },
      { step: "Maintenance", desc: "We mulch, trim, prune, and plant to bring the landscape to life." },
      { step: "Walkthrough", desc: "We confirm every bed and shrub looks clean and complete." },
    ],
  },
  {
    slug: "bed-detailing",
    title: "Bed Detailing",
    subtitle: "Weed-free, well-kept beds",
    price: "Free Estimate",
    image: IMG(38198729),
    description:
      "Landscaping beds look their best when they're clean and consistently maintained. Our detailing service keeps beds free of weeds and your plants in great condition on a recurring schedule — monthly or every two weeks — so your property always looks polished without you lifting a finger.",
    features: [
      "Recurring weed removal from beds",
      "Plant health checks & light pruning",
      "Debris & leaf cleanup from beds",
      "Edge redefinition for crisp bed lines",
      "Monthly or bi-weekly visit options",
      "Consistent, detail-focused crews",
    ],
    process: [
      { step: "Inspection", desc: "We review your beds, plants, and weed pressure." },
      { step: "Schedule", desc: "You choose monthly or every-two-weeks detailing visits." },
      { step: "Detailing", desc: "We pull weeds, tidy plants, and clean each bed thoroughly." },
      { step: "Upkeep", desc: "Ongoing visits keep your landscaping sharp all season." },
    ],
  },
  {
    slug: "turf-applications",
    title: "Turf Applications",
    subtitle: "Fertilizer & weed control",
    price: "Free Estimate",
    image: IMG(38194805),
    description:
      "Grow a thicker, greener, healthier lawn with our turf applications. We apply the right fertilizer and weed control products on a seasonal program to feed your grass, crowd out weeds, and keep your lawn strong through Kansas summers and winters across Johnson County.",
    features: [
      "Seasonal fertilization programs",
      "Broadleaf & grassy weed control",
      "Pre-emergent & post-emergent treatments",
      "Products matched to your grass type",
      "Thicker, greener, more resilient turf",
      "Applied by trained, insured technicians",
    ],
    process: [
      { step: "Lawn Analysis", desc: "We evaluate your turf, weed issues, and soil conditions." },
      { step: "Program Plan", desc: "A seasonal schedule of fertilizer and weed control is built for your lawn." },
      { step: "Application", desc: "We apply the right products at the right time, safely and evenly." },
      { step: "Monitor", desc: "We track results and adjust each round for the best lawn possible." },
    ],
  },
  {
    slug: "chigger-pest-control",
    title: "Chigger & Pest Control",
    subtitle: "Chigger, flea & tick prevention",
    price: "Free Estimate",
    image: IMG(1301856),
    description:
      "Take your yard back from chiggers, fleas, and ticks. Our lawn pest control service targets the biting insects that make Johnson County lawns miserable in summer. We treat turf, bed edges, and shady problem areas on a seasonal schedule so your family and pets can enjoy the yard bite-free across Olathe, Overland Park, and the surrounding area.",
    features: [
      "Targeted chigger treatment for lawns & beds",
      "Flea & tick prevention for yards with pets",
      "Barrier treatments along tree lines & shade",
      "Seasonal programs timed to peak activity",
      "Family- and pet-conscious application methods",
      "Applied by trained, insured technicians",
    ],
    process: [
      { step: "Yard Inspection", desc: "We identify chigger, flea, and tick hot spots around your property." },
      { step: "Treatment Plan", desc: "A seasonal schedule targeting peak biting-insect activity is built for your yard." },
      { step: "Application", desc: "We treat turf, bed edges, and shaded areas safely and evenly." },
      { step: "Monitor", desc: "We track results and re-treat as needed to keep your yard bite-free." },
    ],
  },
];
